package kz.dentalcrm.app.screens.menu

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathFillType.Companion.NonZero
import androidx.compose.ui.graphics.SolidColor
import androidx.compose.ui.graphics.StrokeCap.Companion.Round
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.graphics.vector.ImageVector.Builder
import androidx.compose.ui.graphics.vector.path
import androidx.compose.ui.unit.dp
import compose.icons.FeatherIcons
import compose.icons.feathericons.Activity
import compose.icons.feathericons.Briefcase
import compose.icons.feathericons.FileText
import compose.icons.feathericons.Heart
import compose.icons.feathericons.Layers
import compose.icons.feathericons.LogOut
import compose.icons.feathericons.MessageSquare
import dentalcrm.composeapp.generated.resources.Res
import dentalcrm.composeapp.generated.resources.diagnosis
import dentalcrm.composeapp.generated.resources.dialog_log_out_message
import dentalcrm.composeapp.generated.resources.dialog_log_out_title
import dentalcrm.composeapp.generated.resources.log_out
import dentalcrm.composeapp.generated.resources.menu
import dentalcrm.composeapp.generated.resources.positions
import dentalcrm.composeapp.generated.resources.protocols
import dentalcrm.composeapp.generated.resources.quick_replies
import dentalcrm.composeapp.generated.resources.service_categories
import dentalcrm.composeapp.generated.resources.services
import kz.dentalcrm.app.navigation.AppNavigator
import kz.dentalcrm.app.navigation.DiagnosisRoute
import kz.dentalcrm.app.navigation.PositionsRoute
import kz.dentalcrm.app.navigation.ProtocolsRoute
import kz.dentalcrm.app.navigation.QuickRepliesRoute
import kz.dentalcrm.app.navigation.ServiceCategoriesRoute
import kz.dentalcrm.app.navigation.ServicesRoute
import kz.dentalcrm.app.screens.menu.mvi.MenuViewEvent
import kz.dentalcrm.app.screens.ui.ContentHeader
import kz.dentalcrm.app.screens.ui.LogOutDialog
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.koinInject
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun MenuScreen(
    viewModel: MenuViewModel = koinViewModel()
) {
    val navigator: AppNavigator = koinInject()

    MenuContent(
        navigator = navigator,
        navigateToLogin = { viewModel.setEvent(MenuViewEvent.LogOut) }
    )
}

@Composable
fun MenuContent(
    navigator: AppNavigator,
    navigateToLogin: () -> Unit
) {
    var isLogCalled by remember {
        mutableStateOf(false)
    }

    if (isLogCalled) {
        LogOutDialog(
            title = stringResource(Res.string.dialog_log_out_title),
            message = stringResource(Res.string.dialog_log_out_message),
            confirm = navigateToLogin,
            cancel = {
                isLogCalled = false
            }
        ) { isLogCalled = false }
    }

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(horizontal = 12.dp)
            .safeDrawingPadding()
    ) {
        ContentHeader(
            modifier = Modifier.fillMaxWidth(),
            text = stringResource(Res.string.menu)
        )
        HorizontalDivider(
            modifier = Modifier.fillMaxWidth(),
            thickness = 0.5.dp,
            color = androidx.compose.material3.MaterialTheme.colorScheme.secondaryContainer
        )
        Spacer(
            modifier = Modifier.size(16.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.diagnosis),
            leadingIcon = FeatherIcons.Activity,
            action = {
                navigator.navigate(DiagnosisRoute)
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.services),
            leadingIcon = FeatherIcons.Heart,
            action = {
                navigator.navigate(ServicesRoute)
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.positions),
            leadingIcon = FeatherIcons.Briefcase,
            action = {
                navigator.navigate(PositionsRoute)
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.service_categories),
            leadingIcon = FeatherIcons.Layers,
            action = {
                navigator.navigate(ServiceCategoriesRoute)
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.protocols),
            leadingIcon = FeatherIcons.FileText,
            action = {
                navigator.navigate(ProtocolsRoute)
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.quick_replies),
            leadingIcon = FeatherIcons.MessageSquare,
            action = {
                navigator.navigate(QuickRepliesRoute)
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
        OptionItem(
            modifier = Modifier.fillMaxWidth(),
            title = stringResource(Res.string.log_out),
            leadingIcon = FeatherIcons.LogOut,
            action = {
                isLogCalled = true
            }
        )
        Spacer(
            modifier = Modifier.size(8.dp)
        )
    }
}

@Composable
private fun OptionItem(
    modifier: Modifier,
    title: String,
    leadingIcon: ImageVector,
    action: () -> Unit,
) {
    Button(
        modifier = modifier,
        onClick = action,
        shape = RoundedCornerShape(16.dp),
        contentPadding = PaddingValues(vertical = 16.dp)
    ) {
        Spacer(modifier = Modifier.width(12.dp))
        Icon(
            imageVector = leadingIcon,
            contentDescription = null
        )
        Spacer(modifier = Modifier.width(8.dp))
        Text(
            modifier = Modifier.weight(1f),
            text = title,
        )
        Spacer(modifier = Modifier.width(8.dp))
        Icon(
            imageVector = chevronRight,
            contentDescription = null
        )
        Spacer(modifier = Modifier.width(12.dp))
    }
}

val chevronRight: ImageVector
    get() {
        if (_chevronRight != null) {
            return _chevronRight!!
        }
        _chevronRight = Builder(
            name = "ChevronRight", defaultWidth = 24.0.dp, defaultHeight =
            24.0.dp, viewportWidth = 24.0f, viewportHeight = 24.0f
        ).apply {
            path(
                fill = SolidColor(Color(0x00000000)), stroke = SolidColor(Color(0xFF000000)),
                strokeLineWidth = 2.0f, strokeLineCap = Round, strokeLineJoin =
                StrokeJoin.Companion.Round, strokeLineMiter = 4.0f, pathFillType = NonZero
            ) {
                moveTo(9.0f, 18.0f)
                lineToRelative(6.0f, -6.0f)
                lineToRelative(-6.0f, -6.0f)
            }
        }
            .build()
        return _chevronRight!!
    }

private var _chevronRight: ImageVector? = null
